import type { ApiSchema } from './schema';
import cookies from '../services/cookies';
import { merge } from 'lodash';
import type { AbortablePromise, ArrayWithTotal, RequestParams, SchemaPathTree } from './types';

const API_URL = (import.meta as any).env.VUE_APP_API_URL as string;

function createApiClient(): any {
    function buildProxy(segments: string[]): any {
        return new Proxy({} as any, {
            get(_target, prop: string) {
                if (prop === '$get') {
                    return (params: RequestParams[] = [], config = {}, includeResponse = false) => {
                        return createRequest('GET', {
                            path: segments.join('/'),
                            params,
                            config,
                            includeResponse,
                        });
                    };
                }

                if (prop === '$post') {
                    return (payload: any, params: RequestParams[] = [], config = {}, includeResponse = false) => {
                        return createRequest('POST', {
                            path: segments.join('/'),
                            payload,
                            params,
                            config,
                            includeResponse,
                        });
                    };
                }

                if (prop === '$patch') {
                    return (payload: any, params: RequestParams[] = [], config = {}, includeResponse = false) => {
                        return createRequest('PATCH', {
                            path: segments.join('/'),
                            payload,
                            params,
                            config,
                            includeResponse,
                        });
                    };
                }

                if (prop === '$delete') {
                    return (params: RequestParams[] = [], config = {}, includeResponse = false) => {
                        return createRequest('DELETE', {
                            path: segments.join('/'),
                            params,
                            config,
                            includeResponse,
                        });
                    };
                }

                return buildProxy([...segments, prop]);
            },
        });
    }

    return buildProxy([]);
}

export const apiClient: SchemaPathTree<ApiSchema> = createApiClient();

type CreateRequestOptions = {
    path: string;
    params: RequestParams[];
    config: RequestInit;
    payload?: any;
    includeResponse: boolean;
};

function createRequest(
    method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
    { path, params, config, payload, includeResponse }: CreateRequestOptions,
): AbortablePromise<any> {
    const session = cookies.get('f24_session') as string;
    const url = new URL(`${API_URL}${path}`);
    const controller = new AbortController();
    params.forEach(({ key, value }) => url.searchParams.append(key, value.toString()));

    // A `FormData` payload goes as is, multipart: the browser sets the content type with its boundary
    const isMultipart = payload instanceof FormData;
    const defaultConfig: RequestInit = {
        method,
        credentials: 'omit',
        signal: controller.signal,
        headers: {
            'X-session': session,
            accept: 'application/json, text/plain, */*',
            ...(isMultipart ? {} : { 'content-type': 'application/json' }),
        },
    };
    if (payload !== undefined) defaultConfig.body = isMultipart ? payload : JSON.stringify(payload);

    const requestEndpoint =
        (import.meta as any).env.PROD && (import.meta as any).env.VUE_APP_CONTEXT !== 'deploy-preview' ? url.pathname + url.search : url.href;

    const returnValue = (async () => {
        const response = await fetch(new Request(requestEndpoint, merge(defaultConfig, config)));

        if (!response.ok) {
            const data = await response.json();
            throw data;
        }

        const data = response.status !== 204 ? await response.json() : undefined;
        const total = response.headers.get('X-Total');
        if (total && Array.isArray(data)) {
            (data as ArrayWithTotal<any>).total = parseInt(total, 10);
        }
        return includeResponse ? { data, response } : data;
    })() as AbortablePromise<any>;

    returnValue.abort = (reason?: string) => controller.abort(reason);

    return returnValue;
}
